import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import { getNumberComma } from '../../utils/numberUtils';
import _ from 'lodash';
import { calcMonthAmountsByPct, sumMonthPct } from '../../utils/constCodeUtils';

// 2026-10-23 (250423 피드백 #5, 옵션 ①): 월별 금액을 직접 입력하던 걸, 월별 "비율(%)"을
// 직접 입력하면 연간 총목표매출/총매출이익 기준으로 금액이 자동 계산되는 방식으로 바꿨다.
// 비율 합계가 100%가 아니면 저장할 수 없다(합계는 상위 UserGoalListContainer가 검사한다 —
// 여기서는 보기 쉽게 색으로만 표시).
const UserGoalTable = ({ monthGoals, totalSales, totalProfit, editable, onChangeGoals }) => {
  // Local state to handle editable goals
  const [editableGoals, setEditableGoals] = useState([]);

  useEffect(() => {
    if (Array.isArray(monthGoals)) {
      setEditableGoals(monthGoals);
    }
  }, [monthGoals]);

  const handlePctChange = (month, value) => {
    // 숫자(소수 1자리까지)만 허용
    let raw = value.replace(/[^0-9.]/g, '');
    const parts = raw.split('.');
    if (parts.length > 2) {
      raw = parts[0] + '.' + parts.slice(1).join('');
    }

    const pctArray = editableGoals.map((g) => (g.month === month ? raw : g.pct));
    const recalculated = calcMonthAmountsByPct(totalSales, totalProfit, pctArray.map((p) => Number(p) || 0));
    // 사용자가 입력 중인 원본 문자열(pct)은 그대로 유지해 "0.1" 같은 중간 입력이
    // 지워지지 않게 하고, 계산된 금액만 갱신한다.
    const updated = recalculated.map((g, idx) => ({ ...g, pct: pctArray[idx] }));
    setEditableGoals(updated);
    onChangeGoals?.(updated);
  };

  if (!editableGoals || !editableGoals.length) {
    return null;
  }

  const pctSum = sumMonthPct(editableGoals);
  const isPctValid = pctSum === 100;
  const salesSum = _.sumBy(editableGoals, (g) => Number(g.ptargetSales) || 0);
  const profitSum = _.sumBy(editableGoals, (g) => Number(g.ptargetSalesProfit) || 0);

  return (
    <TableContainer component={Box}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell align="center">월</TableCell>
            <TableCell align="center">배분 비율</TableCell>
            <TableCell align="center">목표 매출</TableCell>
            <TableCell align="center">목표 매출 이익</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {editableGoals.map((goal) => (
            <TableRow
              key={goal.month}
              sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
            >
              <TableCell component="th" scope="row" align="center">
                <Typography variant="subtitle2" color="text.primary" component="span">
                  {goal.month}월
                </Typography>
              </TableCell>

              <TableCell align="right">
                <TextField
                  variant="standard"
                  value={goal.pct ?? ''}
                  disabled={!editable}
                  onChange={(e) => handlePctChange(goal.month, e.target.value)}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                  inputProps={{
                    inputMode: 'decimal',
                    style: { textAlign: 'right', width: 56 },
                  }}
                />
              </TableCell>

              <TableCell align="right">
                <Typography variant="body2">{getNumberComma(goal.ptargetSales)}</Typography>
              </TableCell>

              <TableCell align="right">
                <Typography variant="body2">{getNumberComma(goal.ptargetSalesProfit)}</Typography>
              </TableCell>
            </TableRow>
          ))}

          <TableRow>
            <TableCell align="center">
              <Typography variant="subtitle2">총합</Typography>
            </TableCell>
            <TableCell align="right">
              <Typography
                variant="subtitle2"
                color={isPctValid ? 'success.main' : 'error.main'}
              >
                {pctSum}%
              </Typography>
            </TableCell>
            <TableCell align="right">{getNumberComma(salesSum)}</TableCell>
            <TableCell align="right">{getNumberComma(profitSum)}</TableCell>
          </TableRow>
          {!isPctValid && (
            <TableRow>
              <TableCell colSpan={4} align="right">
                <Typography variant="caption" color="error.main">
                  배분 비율 합계가 100%가 아닙니다. (현재 {pctSum}%) 100%를 맞춰야 저장할 수 있습니다.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default UserGoalTable;
